/**
 * Tutorial Modal Component
 * Welcome modal shown on first login with multi-slide walkthrough
 */
import { useState } from 'react';
import { Briefcase, Zap, FileText, Settings, Download, ChevronRight, X } from 'lucide-react';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (dontShowAgain: boolean) => void;
}

interface Slide {
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}

const slides: Slide[] = [
  {
    icon: <Briefcase className="w-12 h-12 text-blue-500" />,
    title: 'Welcome to SkillSnap!',
    content: (
      <p className="text-gray-600 leading-relaxed">
        SkillSnap helps you create bespoke, tailored resumes for every job
        application. Upload your master resume once, add job descriptions, and
        let AI generate perfectly customized documents in seconds.
      </p>
    ),
  },
  {
    icon: <Zap className="w-12 h-12 text-yellow-500" />,
    title: 'Adding Jobs',
    content: (
      <div className="text-gray-600 leading-relaxed space-y-3">
        <p>There are three ways to add a job to your pipeline:</p>
        <ul className="space-y-2 ml-1">
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">Manual:</span>
            <span>Paste in the company name, title, and job description directly.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">URL:</span>
            <span>Provide a link to the job posting and we will scrape the details automatically.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">Email:</span>
            <span>Forward "Job Alert" emails to <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">jobs@skillsnap.me</code> and we'll scrape out all the jobs we find.</span>
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <FileText className="w-12 h-12 text-green-500" />,
    title: 'Generating Resumes',
    content: (
      <div className="text-gray-600 leading-relaxed space-y-3">
        <p>
          Once a job is added, click the <span className="font-semibold text-gray-700">Generate Resume</span> button
          to create all resume sections in one go. The system uses AI to analyze
          the job description and tailor each section of your resume to match.
        </p>
        <p>
          Generation typically takes just a few seconds. You will see progress
          as each section completes.
        </p>
      </div>
    ),
  },
  {
    icon: <Settings className="w-12 h-12 text-purple-500" />,
    title: 'Customize Your Workflow',
    content: (
      <div className="text-gray-600 leading-relaxed space-y-3">
        <p>Choose which resume sections to include, what to call them, and how they are generated:</p>
        <ul className="space-y-2 ml-1">
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">AI Select:</span>
            <span>AI selects the most relevant items from content you wrote.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">AI Rewrite:</span>
            <span>AI rewrites what you wrote to better match the job description.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">Manual:</span>
            <span>Uses your content exactly as-is, with no AI modifications.</span>
          </li>
        </ul>
        <p>Set these globally for all jobs, then override per job if you prefer.</p>
        <p className="text-gray-500 italic">Senior in your field? You can easily remove all items before a set year, and hide dates from sections like education, awards, and portfolios.</p>
      </div>
    ),
  },
  {
    icon: <Download className="w-12 h-12 text-indigo-500" />,
    title: 'Your Files',
    content: (
      <div className="text-gray-600 leading-relaxed space-y-3">
        <p>After generation, you will have access to your tailored files:</p>
        <ul className="space-y-2 ml-1">
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">HTML:</span>
            <span>A styled, web-viewable resume you can preview in the app.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">PDF:</span>
            <span>A print-ready PDF resume for downloading and submitting.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">Text:</span>
            <span>A plain text version, ideal for pasting into application forms.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-gray-700 shrink-0">Custom Link:</span>
            <span>Each resume generated includes a custom link: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">https://your_name.skillsnap.me/company/job_name</code></span>
          </li>
        </ul>
      </div>
    ),
  },
];

export function TutorialModal({ isOpen, onClose, onComplete }: TutorialModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!isOpen) return null;

  const isLastSlide = currentSlide === slides.length - 1;
  const slide = slides[currentSlide];

  function handleNext() {
    if (!isLastSlide) {
      setCurrentSlide((prev) => prev + 1);
    }
  }

  function handleGotIt() {
    onComplete(true);
    setCurrentSlide(0);
  }

  function handleShowAgain() {
    onComplete(false);
    setCurrentSlide(0);
  }

  function handleSkip() {
    onClose();
    setCurrentSlide(0);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Close tutorial"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Slide content */}
        <div className="px-8 pt-10 pb-6">
          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div className="p-4 bg-gray-50 rounded-2xl">
              {slide.icon}
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
            {slide.title}
          </h2>

          {/* Content */}
          <div className="min-h-[160px]">
            {slide.content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 space-y-4">
          {/* Dot indicators */}
          <div className="flex justify-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                  index === currentSlide
                    ? 'bg-blue-500 w-6'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Buttons */}
          {isLastSlide ? (
            <div className="flex items-center gap-3">
              <button
                onClick={handleShowAgain}
                className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Show this again next time
              </button>
              <button
                onClick={handleGotIt}
                className="flex-1 px-6 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
              >
                Great! Got it
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-6 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
